import React, { useState } from "react";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import {
  Autocomplete,
  Box as MuiBox,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography as MuiTypography,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useStore } from "../../../store";
import DateRangePicker from "../../../components/DateRangePicker";
import KeywordSearch from "./KeywordSearch";
import { getRegionCode } from "../../Templates/RegionCode";
import {
  MATCH_TYPE_OPTIONS,
  LIMIT_RANGE,
  TEMPLATES_PER_KEYWORD_RANGE,
  DEFAULT_RANGE_DAYS,
  MAX_RANGE_DAYS,
  KeywordMatchType,
  matchTypeLabel,
} from "../keywordParams";
import {
  SecondContainer,
  SecondBox,
  ThirdBox,
  Box,
  Typography,
} from "../styles";

/** 요약 줄의 "라벨 + 값" 한 쌍. 구조와 이유는 AI Review / Templates Handler 와 같다. */
const FilterItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <MuiBox
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      mr: "16px",
      whiteSpace: "nowrap",
    }}
  >
    <Typography sx={{ color: "#79747E" }}>{label}</Typography>
    <Typography sx={{ display: "inline-flex", alignItems: "center" }}>
      {children}
    </Typography>
  </MuiBox>
);

/** 실제 국가만 (AllCountryCode / 빈 항목 제외). Autocomplete 옵션. */
export const COUNTRY_OPTIONS = getRegionCode.filter(
  (c) => c.value.length === 2
);

export const countryLabel = (code: string) => {
  const found = COUNTRY_OPTIONS.find((c) => c.value === code);
  return found ? `${found.key} (${found.value})` : code;
};

const Handler = () => {
  const {
    KeywordsStore: {
      country,
      setCountry,
      matchType,
      setMatchType,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      isDateExplicit,
      setDateExplicit,
      applyDefaultDates,
      limit,
      setLimit,
      templatesPerKeyword,
      setTemplatesPerKeyword,
      applyFilters,
      resetFilters,
    },
  } = useStore();

  const [btnOpen, setBtnOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerKey, setPickerKey] = useState(0);

  // DateRangePicker 는 마운트 시에도 현재 값으로 setStartDate/setEndDate 를 한 번
  // 호출한다(no-op). 값이 실제로 바뀐 경우에만 "명시적 날짜" 로 표시해, 사용자가
  // 날짜를 건드리지 않으면 "최근 30일 ~ 오늘" 이 매 조회마다 새로 계산되게 한다.
  const handleStartDateChange = (date: Date) => {
    if (!dayjs(date).isSame(startDate)) setDateExplicit(true);
    setStartDate(date);
  };
  const handleEndDateChange = (date: Date) => {
    if (!dayjs(date).isSame(endDate)) setDateExplicit(true);
    setEndDate(date);
  };

  // 정렬(sortBy)은 여기 없다 — 인기 목록의 지표 헤더 클릭으로 바꾼다.

  const handleOk = () => {
    const message = applyFilters();
    if (message) {
      // 검증 실패: 다이얼로그를 열어 둔 채 메시지만 보여준다.
      setError(message);
      return;
    }
    setError(null);
    setBtnOpen(false);
  };

  const handleClose = () => {
    resetFilters();
    setPickerKey((k) => k + 1);
    setError(null);
    setBtnOpen(false);
  };

  /**
   * 다이얼로그 안 Enter = OK. capture 단계에서 처리하는 이유와 예외 목록은
   * AI Review Handler 의 같은 함수 주석을 따른다.
   */
  const handleDialogKeyDownCapture = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    if (event.nativeEvent.isComposing) return;
    const target = event.target as HTMLElement;
    if (document.querySelector(".MuiPopover-root, .MuiAutocomplete-popper")) {
      return;
    }
    if (target.closest("button")) return;
    if (target.closest(".rdrDateDisplayWrapper")) return;
    event.preventDefault();
    event.stopPropagation();
    handleOk();
  };

  const onNumberChange =
    (setter: (n: number) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const n = Number(event.target.value);
      setter(Number.isNaN(n) ? 0 : n);
    };

  /**
   * 숫자 입력은 min/max 속성으로 범위를 알리고, 포커스가 빠질 때 범위 안으로 잘라
   * 넣는다. 타이핑 중에는 잘라 내지 않는다 — "1"을 치는 순간 min 으로 튀어 버린다.
   */
  const clampOnBlur =
    (setter: (n: number) => void, range: { min: number; max: number }) =>
    (event: React.FocusEvent<HTMLInputElement>) => {
      const n = Number(event.target.value);
      if (Number.isNaN(n)) return;
      setter(Math.min(range.max, Math.max(range.min, Math.round(n))));
    };

  return (
    <>
      <SecondContainer>
        <FilterAltIcon
          onClick={() => setBtnOpen(true)}
          sx={{ cursor: "pointer", color: "#6750a4" }}
        />
        <SecondBox
          sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}
        >
          <ThirdBox
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              flexWrap: "wrap",
              pt: "10px",
            }}
          >
            <FilterItem label="Country">{countryLabel(country)}</FilterItem>
            <FilterItem label="Keyword type">{matchTypeLabel(matchType)}</FilterItem>
            <FilterItem label="Published">
              {dayjs(startDate).format("YYYY-MM-DD") +
                " ~ " +
                dayjs(endDate).format("YYYY-MM-DD")}
              {!isDateExplicit && (
                <MuiTypography
                  component="span"
                  sx={{ color: "#79747E", ml: "4px", fontSize: 12 }}
                >
                  (last {DEFAULT_RANGE_DAYS} days)
                </MuiTypography>
              )}
            </FilterItem>
            <FilterItem label="Keywords">{limit}</FilterItem>
            <FilterItem label="Templates / keyword">
              {templatesPerKeyword}
            </FilterItem>
          </ThirdBox>
        </SecondBox>
        {/* 다른 리스트 페이지처럼 요약 줄 오른쪽 끝에 검색을 둔다. */}
        <KeywordSearch />
      </SecondContainer>

      <Dialog
        open={btnOpen}
        onClose={handleClose}
        onKeyDownCapture={handleDialogKeyDownCapture}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "900px",
            minWidth: "900px",
            maxHeight: "650px",
            minHeight: "650px",
          },
        }}
      >
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
        >
          <Box>
            <Autocomplete
              disablePortal
              disableClearable
              size="small"
              id="combo-box-country"
              value={
                // 항상 controlled 로 두기 위해 목록에 없는 코드도 그대로 감싸서 준다.
                COUNTRY_OPTIONS.find((c) => c.value === country) ?? {
                  key: country,
                  value: country,
                }
              }
              options={COUNTRY_OPTIONS}
              onChange={(_, value) => {
                if (value) setCountry(value.value);
              }}
              getOptionLabel={(option) => `${option.key} (${option.value})`}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField {...params} label="Country" required />
              )}
              sx={{
                "& .MuiFormControl-root.MuiTextField-root": {
                  width: 300,
                  mr: "4px",
                },
              }}
            />
            {/* 선택지가 둘뿐이라 Select 대신 라디오로 한눈에 보이게 한다. */}
            {/* 부모(Box)가 flex 행이라 FormControl 이 줄어들면 RadioGroup 이 wrap 되어
                세로로 꺾인다. 줄어들지 않게 하고 wrap 도 막는다. */}
            <FormControl sx={{ mr: "8px", flexShrink: 0 }}>
              <FormLabel id="keyword-type-label" sx={{ fontSize: 12 }}>
                Keyword type
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="keyword-type-label"
                name="keyword-type"
                value={matchType}
                onChange={(_, value) => setMatchType(value as KeywordMatchType)}
                sx={{ flexWrap: "nowrap" }}
              >
                {MATCH_TYPE_OPTIONS.map((o) => (
                  <FormControlLabel
                    key={o.value}
                    value={o.value}
                    control={<Radio size="small" />}
                    label={o.label}
                    sx={{ "& .MuiTypography-root": { fontSize: 14 } }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label="Keywords"
              value={limit}
              onChange={onNumberChange(setLimit)}
              onBlur={clampOnBlur(setLimit, LIMIT_RANGE)}
              inputProps={{ min: LIMIT_RANGE.min, max: LIMIT_RANGE.max, step: 1 }}
              sx={{ "&.MuiFormControl-root.MuiTextField-root": { width: 120 } }}
            />
            <TextField
              size="small"
              type="number"
              label="Templates / keyword"
              value={templatesPerKeyword}
              onChange={onNumberChange(setTemplatesPerKeyword)}
              onBlur={clampOnBlur(
                setTemplatesPerKeyword,
                TEMPLATES_PER_KEYWORD_RANGE
              )}
              inputProps={{
                min: TEMPLATES_PER_KEYWORD_RANGE.min,
                max: TEMPLATES_PER_KEYWORD_RANGE.max,
                step: 1,
              }}
              sx={{ "&.MuiFormControl-root.MuiTextField-root": { width: 160 } }}
            />
          </Box>

          <DialogContent
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              pl: 0,
              pt: 1,
            }}
          >
            <MuiBox
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
            >
              <MuiTypography variant="caption" sx={{ color: "#79747E" }}>
                Published date (UTC). Up to {MAX_RANGE_DAYS} days. The end date
                is included through 23:59:59.
              </MuiTypography>
              {isDateExplicit && (
                <Button
                  size="small"
                  onClick={() => {
                    applyDefaultDates();
                    setPickerKey((k) => k + 1);
                  }}
                  sx={{ textTransform: "none", fontSize: 12, p: "0 6px" }}
                >
                  Reset to last {DEFAULT_RANGE_DAYS} days
                </Button>
              )}
            </MuiBox>
            <DateRangePicker
              // 리셋 버튼으로 날짜가 바뀌면 피커 내부 state 도 다시 만들어야 한다.
              // (선택마다 바뀌는 값을 key 로 쓰면 범위 선택 도중 remount 되므로 금지)
              key={pickerKey}
              startDate={startDate}
              endDate={endDate}
              setStartDate={handleStartDateChange}
              setEndDate={handleEndDateChange}
              maxRangeDays={MAX_RANGE_DAYS}
            />
          </DialogContent>

          {error && (
            <MuiTypography
              role="alert"
              variant="body2"
              sx={{ color: "#B3261E", mt: 1 }}
            >
              {error}
            </MuiTypography>
          )}
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleOk}>OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default observer(Handler);
